/**
 * LES TROIS AUTRES VUES DE L'ÉTAT DU STOCK — grille de tailles, tableau croisé, stock distant.
 *
 * ⚠️ PORTAGE VERBATIM DE LA MAQUETTE (`design/`, écran `stock2`), comme `ecarts.tsx` : mêmes
 * boîtes, mêmes tailles, mêmes teintes, tracés SVG repris tels quels via `Trait`. La première version
 * « traduisait » la maquette en composants de charte — et l'écran ne ressemblait plus à ce qui avait
 * été validé. On copie, on ne réinterprète pas ; seules les adaptations imposées par les données
 * réelles sont permises, et chacune est marquée ⚠️.
 *
 * Elles partagent le FILTRE et rien d'autre — c'est lui qui garantit qu'un total ne change pas selon
 * l'onglet regardé.
 */
import { useCallback, useEffect, useId, useState } from 'react';
import { EmptyState, showToast, Trans } from '@orgazone/ui-web';
import { formatMoney } from '@orgazone/money-format';
import {
  api, type SavedStockView, type StockGridSection, type StockPivot, type StockPivotDimension,
  type StockPivotMeasure, type StockQuery, type StockRemoteStore,
} from '../api';
import { fieldLabel } from '../vocabulary';
import { key, t, type Translatable } from '../i18n';

/**
 * Le séparateur des clés composites « article + couleur ».
 *
 * ⚠️ FABRIQUÉ, JAMAIS ÉCRIT EN CLAIR. Il faut un caractère qu'aucun libellé d'article ou de coloris ne
 * peut porter — sans quoi « ROBE|NOIR » et « ROBE » de couleur « NOIR » collideraient. Mais poser un
 * octet de contrôle dans le fichier source en fait un caractère invisible que la prochaine traversée
 * de guillemets peut manger sans rien casser à la compilation (Forme E du catalogue). On le construit
 * donc, et le nom dit à quoi il sert.
 */
const CLE_SEP = String.fromCharCode(31); // séparateur d'unité (US)

/** Un pictogramme au trait — même fabrique que `ecarts.tsx`, tracés repris de la maquette. */
export function Trait({ d, size = 15, stroke = 1.9 }: { d: string; size?: number; stroke?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
  );
}

/**
 * La pastille de FAMILLE de la liste (maquette : `FAMC`).
 * ⚠️ La maquette peint six familles de démonstration nommées en dur ; les vraies familles sont
 * libres. On garde sa palette, indexée par un hachage STABLE du nom — la même famille garde la même
 * teinte d'une visite à l'autre. Décor uniquement : l'information reste le texte à côté.
 */
const FAMILY_DOT_PALETTE = [
  'var(--blue)', 'var(--violet)', 'oklch(0.70 0.13 320)',
  'var(--teal-700)', 'var(--teal-500)', 'oklch(0.72 0.13 200)',
];
export function familyDot(family: string | null): string {
  if (!family) return 'var(--ink-300)';
  let h = 0;
  for (let i = 0; i < family.length; i += 1) h = (h * 31 + family.charCodeAt(i)) >>> 0;
  return FAMILY_DOT_PALETTE[h % FAMILY_DOT_PALETTE.length]!;
}

/**
 * Les axes du croisé, dans l'ordre des menus. Chaque axe EST un champ du catalogue : son mot vient du
 * vocabulaire du magasin et se résout au rendu par `axisLabel(axe)` — jamais le nom de colonne.
 * ⚠️ AUCUN LIBELLÉ ICI, à dessein : une liste `{ axe, mot }` figée au chargement du module dirait
 *    « Saison » chez le caviste, avant que le contexte magasin n'ait posé « Millésime ».
 */
export const AXES: readonly StockPivotDimension[] = ['family', 'brand', 'size', 'color', 'season'];

/** Le mot du magasin pour un axe du croisé — les deux menus et l'en-tête des lignes passent tous par lui. */
export function axisLabel(axis: StockPivotDimension): string {
  return fieldLabel(axis);
}
export const MESURES: { v: StockPivotMeasure; l: Translatable }[] = [
  { v: 'qty', l: key('Quantité') }, { v: 'valuePmp', l: key('Valeur au PMP') }, { v: 'valueSale', l: key('Valeur au PV TTC') },
];

/** Une mesure monétaire s'affiche en euros ; une quantité reste un nombre. */
const mesureLue = (v: number, m: StockPivotMeasure): string =>
  m === 'qty' ? Math.round(v).toLocaleString('fr-FR') : formatMoney(v, { thousandsSep: ' ' });

/** L'encre d'une quantité (maquette) : rose en négatif, effacée à zéro, pleine sinon. */
const qtyInk = (q: number): string =>
  q < 0 ? 'var(--rose-fg)' : q === 0 ? 'var(--ink-300)' : 'var(--ink-900)';

/** L'en-tête de section de grille (maquette V2/V4) : carte blanche, titre 14/700 + mention 12. */
function SectionHeader({ title, note }: { title: string; note: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '13px 18px', borderBottom: '1px solid var(--ink-200)' }}>
      <span style={{ flex: 1, fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)' }}>{title}</span>
      <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{note}</span>
    </div>
  );
}

/** Le libellé d'en-tête de colonne de grille (10.5/700, majuscules espacées) — maquette. */
const GRID_HEAD: React.CSSProperties = {
  fontSize: '10.5px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)',
};

// ── LA GRILLE DE TAILLES (maquette `stV2`) ─────────────────────────────────────────────────────

/**
 * Un modèle × une couleur par ligne, ses tailles en colonnes, corrigeable case par case.
 *
 * ⚠️ UNE CASE VIDE (`—`) N'EST PAS UN ZÉRO : elle dit « cette taille n'existe pas dans ce coloris ».
 * On ne peut pas y saisir — corriger une déclinaison inexistante écrirait un mouvement sur un
 * gencode que le catalogue ne connaît pas. La maquette n'a pas ce cas (démo pleine) ; le vrai
 * catalogue l'a.
 * ⚠️ SECTIONS PAR FAMILLE et non par gabarit de grille : le back-office ne connaît pas les grilles
 * de tailles (concept caisse) — la famille est le seul regroupement dont il dispose.
 */
export function GrilleDesTailles({ filtre, saisies, setSaisie, lectureSeule }: {
  filtre: StockQuery;
  /** La saisie porte le stock d'AVANT : la barre de corrections n'a pas à le rechercher ailleurs. */
  saisies: Record<string, { before: number; value: string }>;
  setSaisie: (gencode: string, before: number, value: string) => void;
  /** Stock à une date passée : la grille se lit, elle ne se corrige pas. */
  lectureSeule: boolean;
}) {
  const [sections, setSections] = useState<StockGridSection[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    setErreur(null);
    api.stockGrid(filtre)
      .then((r) => { if (vivant) setSections(r.sections); })
      .catch((e: unknown) => { if (vivant) setErreur(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [filtre]);

  if (erreur) return <EmptyState icon="alert" title={t('Grille illisible')} note={t('Le magasin n’a pas répondu : {erreur}', { erreur })} />;
  if (!sections) return <div style={{ padding: 30, fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Lecture de la grille…')}</div>;
  if (sections.length === 0) {
    return <EmptyState icon="box" title={t('Aucune référence ne correspond')} note={t('Retirez un filtre — un stock vide ne se cache jamais derrière un filtre oublié.')} />;
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 12 }}>
      {sections.map((sec) => (
        <div key={sec.title} style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
          <SectionHeader title={sec.title} note={t('{n} ligne · total par ligne au bout', { n: sec.rows.length })} />
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 540 }}>
              <div style={{ display: 'grid', gridTemplateColumns: `minmax(180px,1.4fr) repeat(${sec.sizes.length},minmax(56px,1fr)) 76px`, gap: 6, padding: '9px 18px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)' }}>
                <div style={GRID_HEAD}>{t('Article · couleur')}</div>
                {sec.sizes.map((size) => (
                  <div key={size} style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', fontWeight: 700, color: 'var(--ink-500)' }}>{size}</div>
                ))}
                <div style={{ ...GRID_HEAD, textAlign: 'right', fontWeight: 800, color: 'var(--ink-700)' }}>{t('Total')}</div>
              </div>
              {sec.rows.map((r, ri) => {
                // ⚠️ Maquette : la 1ʳᵉ couleur d'un modèle ouvre le groupe (filet + nom plein), les
                //    suivantes répètent le nom en retrait de teinte — on repère le modèle d'un coup d'œil.
                const first = ri === 0 || sec.rows[ri - 1]!.name !== r.name;
                // Les corrections en attente déplacent le total de ligne EN DIRECT (la maquette le promet).
                const total = r.cells.reduce((sum, c) => {
                  if (c.qty == null || !c.gencode) return sum;
                  const s2 = saisies[c.gencode];
                  const v = s2 && s2.value !== '' && Number.isFinite(Number(s2.value)) ? Math.trunc(Number(s2.value)) : c.qty;
                  return sum + v;
                }, 0);
                return (
                  <div
                    key={`${r.name}|${r.color ?? ''}`}
                    style={{
                      display: 'grid', gridTemplateColumns: `minmax(180px,1.4fr) repeat(${sec.sizes.length},minmax(56px,1fr)) 76px`,
                      gap: 6, padding: '5px 18px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)',
                      borderTop: first ? '1px solid var(--ink-200)' : 'none',
                    }}
                  >
                    <div style={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                      <span style={{ fontSize: '12.5px', fontWeight: first ? 600 : 400, color: first ? 'var(--ink-900)' : 'var(--ink-400)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.name}</span>
                      {r.color ? <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', whiteSpace: 'nowrap', flexShrink: 0 }}>· {r.color}</span> : null}
                    </div>
                    {r.cells.map((c) => {
                      if (c.qty == null || !c.gencode) {
                        return (
                          <div key={c.size} style={{ display: 'flex', justifyContent: 'center' }}>
                            <span title={t('Cette taille n’existe pas dans ce coloris')} style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: 62, height: 32, color: 'var(--ink-300)' }}>—</span>
                          </div>
                        );
                      }
                      const saisie = saisies[c.gencode]?.value ?? '';
                      const pend = saisie !== '' && Number.isFinite(Number(saisie)) && Math.trunc(Number(saisie)) !== c.qty;
                      return (
                        <div key={c.size} style={{ display: 'flex', justifyContent: 'center' }}>
                          {lectureSeule ? (
                            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%', maxWidth: 62, height: 32, borderRadius: 'var(--r-xs)', background: 'var(--ink-50)', fontSize: '12.5px', fontWeight: 700, color: qtyInk(c.qty), fontVariantNumeric: 'tabular-nums' }}>{c.qty}</span>
                          ) : (
                            <input
                              aria-label={t('Quantité en stock — {v}', { v: `${r.name}${r.color ? ` · ${r.color}` : ''} · ${c.size}` })}
                              value={saisie === '' ? String(c.qty) : saisie}
                              onChange={(e) => setSaisie(c.gencode!, c.qty!, e.target.value.replace(/[^\d-]/g, ''))}
                              inputMode="numeric"
                              style={{
                                width: '100%', maxWidth: 62, height: 32, textAlign: 'center',
                                border: `1px solid ${pend ? 'var(--amber)' : c.qty < 0 ? 'var(--rose)' : 'var(--ink-200)'}`,
                                borderRadius: 'var(--r-xs)', background: pend ? 'var(--amber-bg)' : 'var(--paper)',
                                color: qtyInk(c.qty), fontSize: '12.5px', fontWeight: 700, outline: 'none', fontVariantNumeric: 'tabular-nums',
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                    <div style={{ textAlign: 'right', fontSize: 'var(--t-lead)', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: total < 0 ? 'var(--rose-fg)' : 'var(--ink-900)' }}>{total}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      ))}
      <div style={{ padding: '13px 17px', borderRadius: 'var(--r-md)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.6 }}>
        <Trans k='Corriger une cellule crée un mouvement <1>sur la déclinaison précise</1> — taille et couleur comprises. Le total de ligne suit en direct.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
    </div>
  );
}

// ── LE TABLEAU CROISÉ (maquette `stV3`) ────────────────────────────────────────────────────────

/** Le sélecteur natif des axes — style maquette (36 px, bord ink-200). */
const PIVOT_SELECT: React.CSSProperties = {
  height: 36, padding: '0 10px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)',
  background: 'var(--paper)', color: 'var(--ink-900)', fontSize: 'var(--t-lead)', fontWeight: 600, outline: 'none',
};

/**
 * Deux axes, une mesure — agrégé PAR LE SERVEUR sur tout le filtre.
 *
 * ⚠️ RIEN N'EST CROISÉ ICI. Croiser côté écran ne porterait que sur les lignes chargées : le tableau
 * afficherait des totaux qui ont l'air complets et ne le sont pas — un faux qui ne se voit jamais,
 * puisqu'un nombre a toujours l'air d'un nombre.
 */
export function TableauCroise({ filtre }: { filtre: StockQuery }) {
  /** Le préfixe des identifiants qui relient chaque champ au texte qui le présente. */
  const fieldId = useId();
  const [rows, setRows] = useState<StockPivotDimension>('family');
  const [cols, setCols] = useState<StockPivotDimension>('size');
  const [measure, setMeasure] = useState<StockPivotMeasure>('qty');
  const [data, setData] = useState<StockPivot | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [vues, setVues] = useState<SavedStockView[]>([]);
  const [nom, setNom] = useState('');

  const relireVues = useCallback(() => {
    api.stockViews()
      .then((r) => setVues(r.views))
      .catch((e: unknown) => showToast(t('Vues enregistrées illisibles : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err'));
  }, []);
  useEffect(relireVues, [relireVues]);

  const enregistrerVue = () => {
    const n = nom.trim();
    if (!n) return;
    api.saveStockView(n, rows, cols, measure)
      .then(() => { setNom(''); relireVues(); showToast(t('Vue « {nom} » enregistrée — toute l’équipe la retrouvera.', { nom: n }), 'ok'); })
      // ⚠️ La cause RÉELLE du refus : le serveur nomme ce qui cloche (axes identiques, nom trop long).
      .catch((e: unknown) => showToast(t('Vue non enregistrée : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err'));
  };

  useEffect(() => {
    let vivant = true;
    setErreur(null);
    api.stockPivot(rows, cols, measure, filtre)
      .then((d) => { if (vivant) setData(d); })
      .catch((e: unknown) => { if (vivant) setErreur(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [rows, cols, measure, filtre]);

  const nc = data?.colLabels.length ?? 1;
  const grille = `150px repeat(${nc},minmax(86px,1fr)) 96px`;

  return (
    <div style={{ overflow: 'hidden' }}>
      {/* Les axes en tête de carte — libellés « Lignes × Colonnes · Mesure », comme la maquette. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '13px 18px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
        <span id={`${fieldId}-0`} style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{t('Lignes')}</span>
        <select aria-labelledby={`${fieldId}-0`} value={rows} onChange={(e) => setRows(e.target.value as StockPivotDimension)} style={PIVOT_SELECT}>
          {AXES.map((a) => <option key={a} value={a}>{axisLabel(a)}</option>)}
        </select>
        <span id={`${fieldId}-1`} style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{t('× Colonnes')}</span>
        {/* ⚠️ L'axe déjà pris en LIGNE est retiré : croiser un axe avec lui-même ne croise rien, et le
            serveur le refuse. Le proposer serait offrir une option qui échoue (§23.0). */}
        <select aria-labelledby={`${fieldId}-1`} value={cols} onChange={(e) => setCols(e.target.value as StockPivotDimension)} style={PIVOT_SELECT}>
          {AXES.filter((a) => a !== rows).map((a) => <option key={a} value={a}>{axisLabel(a)}</option>)}
        </select>
        <span id={`${fieldId}-2`} style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{t('· Mesure')}</span>
        <select aria-labelledby={`${fieldId}-2`} value={measure} onChange={(e) => setMeasure(e.target.value as StockPivotMeasure)} style={PIVOT_SELECT}>
          {MESURES.map((m) => <option key={m.v} value={m.v}>{t(m.l)}</option>)}
        </select>
        <div style={{ flex: 1 }} />
        {vues.map((v) => {
          const on = v.rowDim === rows && v.colDim === cols && v.measure === measure;
          return (
            <span
              key={v.name}
              style={{
                display: 'inline-flex', alignItems: 'center', height: 32, borderRadius: 'var(--r-pill)',
                border: `1.5px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`,
                background: on ? 'var(--teal-100)' : 'var(--paper)', whiteSpace: 'nowrap',
              }}
            >
              <button
                type="button" title={t('Par {v}', { v: v.createdBy || t('l’équipe') })}
                onClick={() => { setRows(v.rowDim); setCols(v.colDim); setMeasure(v.measure); }}
                style={{ height: '100%', padding: '0 3px 0 12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 'var(--t-dense)', fontWeight: 600, color: on ? 'var(--teal-800)' : 'var(--ink-600)' }}
              >
                {v.name}
              </button>
              {/* ⚠️ ABSENT DE LA MAQUETTE, gardé quand même : une vue PARTAGÉE que personne ne peut
                  retirer devient une vue morte que toute l'équipe traîne. Discret à dessein. */}
              <button
                type="button" aria-label={t('Supprimer la vue {name}', { name: v.name })}
                onClick={() => api.deleteStockView(v.name).then(relireVues).catch((e: unknown) => showToast(t('Suppression refusée : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err'))}
                style={{ height: '100%', padding: '0 9px 0 4px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 'var(--t-dense)', color: 'var(--ink-400)' }}
              >
                ×
              </button>
            </span>
          );
        })}
      </div>

      {erreur ? (
        <EmptyState icon="alert" title={t('Croisé illisible')} note={t('Le magasin n’a pas répondu : {erreur}', { erreur })} />
      ) : !data ? (
        <div style={{ padding: 24, fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Agrégation…')}</div>
      ) : data.rowLabels.length === 0 ? (
        <EmptyState icon="chart" title={t('Rien à croiser')} note={t('Aucune référence ne correspond au filtre courant.')} />
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <div style={{ minWidth: 560 }}>
            <div style={{ display: 'grid', gridTemplateColumns: grille, gap: 0, background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)' }}>
              <div style={{ ...GRID_HEAD, padding: '10px 14px' }}>{axisLabel(rows)}</div>
              {data.colLabels.map((c) => (
                <div key={c} style={{ ...GRID_HEAD, padding: '10px 8px', textAlign: 'right', letterSpacing: '.03em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c}</div>
              ))}
              <div style={{ ...GRID_HEAD, padding: '10px 14px', textAlign: 'right', fontWeight: 800, color: 'var(--ink-700)' }}>{t('Total')}</div>
            </div>
            {data.rowLabels.map((rl, i) => (
              <div key={rl} style={{ display: 'grid', gridTemplateColumns: grille, gap: 0, borderBottom: '1px solid var(--ink-100)' }}>
                <div style={{ padding: '10px 14px', fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rl}</div>
                {data.cells[i]!.map((v, j) => (
                  <div key={data.colLabels[j]} style={{ padding: '10px 8px', textAlign: 'right', fontSize: '12.5px', color: v === 0 ? 'var(--ink-300)' : 'var(--ink-800)', fontVariantNumeric: 'tabular-nums' }}>
                    {v === 0 ? '—' : mesureLue(v, measure)}
                  </div>
                ))}
                <div style={{ padding: '10px 14px', textAlign: 'right', fontSize: '12.5px', fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>
                  {mesureLue(data.rowTotals[i]!, measure)}
                </div>
              </div>
            ))}
            <div style={{ display: 'grid', gridTemplateColumns: grille, gap: 0, background: 'var(--ink-50)' }}>
              <div style={{ padding: '11px 14px', fontSize: '12.5px', fontWeight: 800, color: 'var(--ink-900)' }}>{t('Total')}</div>
              {data.colTotals.map((v, j) => (
                <div key={data.colLabels[j]} style={{ padding: '11px 8px', textAlign: 'right', fontSize: '12.5px', fontWeight: 700, color: 'var(--ink-800)', fontVariantNumeric: 'tabular-nums' }}>
                  {mesureLue(v, measure)}
                </div>
              ))}
              <div style={{ padding: '11px 14px', textAlign: 'right', fontSize: 'var(--t-lead)', fontWeight: 800, color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>
                {mesureLue(data.grandTotal, measure)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enregistrer la vue — bandeau de pied de carte (maquette). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '12px 18px', background: 'var(--ink-50)', borderTop: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
        <input
          value={nom} onChange={(e) => setNom(e.target.value)} placeholder={t('Nommer cette vue…')} maxLength={40}
          onKeyDown={(e) => { if (e.key === 'Enter') enregistrerVue(); }}
          style={{ flex: 1, minWidth: 170, height: 36, padding: '0 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--ink-900)', fontSize: '12.5px', outline: 'none' }}
        />
        <button
          type="button" onClick={enregistrerVue} disabled={!nom.trim()}
          style={{ height: 36, padding: '0 13px', border: '1px solid var(--teal-400)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', color: 'var(--teal-800)', fontSize: '12.5px', fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap', cursor: nom.trim() ? 'pointer' : 'default', opacity: nom.trim() ? 1 : 0.55 }}
        >
          
          {t('Enregistrer la vue')}
        </button>
        <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', flexShrink: 0 }}>{t('Les vues enregistrées sont partagées avec toute l’équipe.')}</span>
      </div>
      {data && data.unvalued > 0 && (
        <div style={{ padding: '8px 18px', fontSize: 'var(--t-dense)', color: 'var(--amber-fg)' }}>
          {t('{n} référence non valorisée — hors total', { n: data.unvalued })}</div>
      )}
    </div>
  );
}

// ── LE STOCK DISTANT (maquette `stV4`) ─────────────────────────────────────────────────────────

/**
 * La même grille, chez soi ET chez les magasins qu'on a le droit d'ouvrir — site par site.
 *
 * ⚠️ LECTURE SEULE, ET CE N'EST PAS UNE POLITESSE : on ne corrige que son propre stock. Un mouvement
 * écrit chez le voisin depuis un écran de consultation serait un mouvement que personne, là-bas,
 * n'aurait demandé ni ne pourrait expliquer.
 * ⚠️ UN MAGASIN INJOIGNABLE LE DIT. Le retirer silencieusement de la comparaison ferait lire
 * « le voisin n'en a pas » là où la bonne réponse est « on n'a pas pu lui demander ».
 */
export function StockDistant({ filtre, ownName }: { filtre: StockQuery; ownName: string }) {
  const [magasins, setMagasins] = useState<{ gln: string; name: string }[]>([]);
  const [choisis, setChoisis] = useState<string[]>([]);
  const [own, setOwn] = useState<StockGridSection[] | null>(null);
  const [data, setData] = useState<StockRemoteStore[] | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    api.stockRemoteStores()
      .then((r) => setMagasins(r.stores))
      .catch((e: unknown) => showToast(t('Magasins illisibles : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err'));
  }, []);

  // ⚠️ NOTRE grille est TOUJOURS là (maquette : « toutes vos références, y compris celles à zéro ») —
  //    c'est justement quand on n'a plus rien qu'on regarde chez les autres.
  useEffect(() => {
    let vivant = true;
    setErreur(null);
    api.stockGrid(filtre)
      .then((r) => { if (vivant) setOwn(r.sections); })
      .catch((e: unknown) => { if (vivant) setErreur(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [filtre]);

  useEffect(() => {
    if (choisis.length === 0) { setData([]); return; }
    let vivant = true;
    api.stockRemote(choisis, filtre)
      .then((r) => { if (vivant) setData(r.stores); })
      .catch((e: unknown) => { if (vivant) setErreur(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [choisis, filtre]);

  const basculer = (gln: string) =>
    setChoisis((p) => (p.includes(gln) ? p.filter((x) => x !== gln) : [...p, gln]));

  const injoignables = (data ?? []).filter((s2) => s2.error);

  // La fusion : pour chaque section (famille), chaque ligne article·couleur devient N lignes — la
  // nôtre d'abord, puis un site par magasin comparé (maquette : première ligne seule nommée).
  const sections = (own ?? []).map((sec) => {
    const sitesJoignables = (data ?? []).filter((s2) => !s2.error);
    const parSite = sitesJoignables.map((s2) => {
      const s3 = s2.sections.find((x) => x.title === sec.title);
      return { name: s2.name, rows: new Map((s3?.rows ?? []).map((r) => [`${r.name}${CLE_SEP}${r.color ?? ''}`, r])), sizes: s3?.sizes ?? [] };
    });
    // L'union des tailles : une taille présente seulement chez le voisin doit AUSSI avoir sa colonne.
    const sizes = [...new Set([...sec.sizes, ...parSite.flatMap((s2) => s2.sizes)])];
    return { title: sec.title, sizes, ownRows: sec.rows, parSite };
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, padding: 12 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 38, padding: '0 13px', border: '1.5px solid var(--teal-400)', borderRadius: 'var(--r-sm)', background: 'var(--teal-100)', color: 'var(--teal-800)', fontSize: '12.5px', fontWeight: 700, whiteSpace: 'nowrap' }}>
          {t('{ownName} — nous', { ownName })}</span>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {magasins.map((m) => {
            const on = choisis.includes(m.gln);
            return (
              <button
                key={m.gln} type="button" onClick={() => basculer(m.gln)} aria-pressed={on}
                style={{
                  height: 38, padding: '0 13px', border: `1.5px solid ${on ? 'var(--teal-400)' : 'var(--ink-200)'}`,
                  borderRadius: 'var(--r-sm)', background: on ? 'var(--teal-100)' : 'var(--paper)',
                  color: on ? 'var(--teal-800)' : 'var(--ink-500)', fontSize: '12.5px', fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer',
                }}
              >
                {m.name}
              </button>
            );
          })}
        </div>
        <span style={{ fontSize: '11.5px', color: 'var(--ink-500)' }}>{t('Choisissez les magasins à comparer au vôtre')}</span>
      </div>

      {injoignables.map((s2) => (
        <div key={s2.gln} style={{ padding: '10px 14px', borderRadius: 'var(--r-sm)', border: '1px solid var(--rose)', background: 'var(--rose-bg)', fontSize: '12.5px', color: 'var(--rose-fg)' }}>
          {/* ⚠️ DEUX CLÉS, ET NON UNE CHAÎNE VIDE : sans motif, la forme précédente rendait « X
              injoignable — » — un tiret suivi de rien, qui se lit comme un affichage tronqué. Dire
              que le motif n'est pas remonté est un fait ; laisser le tiret pendre n'en est pas un (§23.0). */}
          {s2.error
            ? <Trans k='<1>{name}</1> injoignable — {error}' vars={{ name: s2.name, error: s2.error }} parts={[(x) => <strong>{x}</strong>]} />
            : <Trans k='<1>{name}</1> injoignable — motif non remonté' vars={{ name: s2.name }} parts={[(x) => <strong>{x}</strong>]} />}
        </div>
      ))}

      {erreur ? (
        <EmptyState icon="alert" title={t('Comparaison impossible')} note={erreur} />
      ) : own == null || data == null ? (
        <div style={{ padding: 24, fontSize: 'var(--t-lead)', color: 'var(--ink-500)' }}>{t('Lecture des magasins…')}</div>
      ) : sections.length === 0 ? (
        <EmptyState icon="box" title={t('Aucune référence ne correspond')} note={t('Retirez un filtre — un stock vide ne se cache jamais derrière un filtre oublié.')} />
      ) : (
        sections.map((sec) => (
          <div key={sec.title} style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' }}>
            <SectionHeader title={sec.title} note={t('{n} site comparé', { n: 1 + (data.filter((s2) => !s2.error).length) })} />
            <div style={{ overflowX: 'auto' }}>
              <div style={{ minWidth: 620 }}>
                <div style={{ display: 'grid', gridTemplateColumns: `minmax(170px,1.3fr) 128px repeat(${sec.sizes.length},minmax(50px,1fr)) 68px`, gap: 6, padding: '9px 18px', background: 'var(--ink-50)', borderBottom: '1px solid var(--ink-200)' }}>
                  <div style={GRID_HEAD}>{t('Article · couleur')}</div>
                  <div style={GRID_HEAD}>{t('Site')}</div>
                  {sec.sizes.map((size) => (
                    <div key={size} style={{ textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-meta)', fontWeight: 700, color: 'var(--ink-500)' }}>{size}</div>
                  ))}
                  <div style={{ ...GRID_HEAD, textAlign: 'right', fontWeight: 800, color: 'var(--ink-700)' }}>{t('Total')}</div>
                </div>
                {sec.ownRows.flatMap((r) => {
                  const cle = `${r.name}${CLE_SEP}${r.color ?? ''}`;
                  const lines = [
                    { site: t('{ownName} — nous', { ownName }), own: true, cells: sec.sizes.map((size) => r.cells.find((c) => c.size === size)?.qty ?? null) },
                    ...sec.parSite.map((s2) => {
                      const rr = s2.rows.get(cle);
                      return { site: s2.name, own: false, cells: sec.sizes.map((size) => rr?.cells.find((c) => c.size === size)?.qty ?? null) };
                    }),
                  ];
                  return lines.map((l, li) => {
                    const total = l.cells.reduce<number>((sum, q) => sum + (q ?? 0), 0);
                    return (
                      <div
                        key={`${cle}|${l.site}`}
                        style={{
                          display: 'grid', gridTemplateColumns: `minmax(170px,1.3fr) 128px repeat(${sec.sizes.length},minmax(50px,1fr)) 68px`,
                          gap: 6, padding: '5px 18px', alignItems: 'center', borderBottom: '1px solid var(--ink-100)',
                          borderTop: li === 0 ? '1px solid var(--ink-200)' : 'none',
                          background: l.own ? 'var(--paper)' : 'var(--ink-50)',
                        }}
                      >
                        <div style={{ minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                          <span style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{li === 0 ? r.name : ''}</span>
                          {li === 0 && r.color ? <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', whiteSpace: 'nowrap', flexShrink: 0 }}>· {r.color}</span> : null}
                        </div>
                        <div style={{ fontSize: 'var(--t-dense)', fontWeight: l.own ? 700 : 400, color: l.own ? 'var(--teal-800)' : 'var(--ink-600)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.site}</div>
                        {l.cells.map((q, ci) => (
                          <div key={sec.sizes[ci]} style={{ textAlign: 'center', fontSize: '12.5px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: q == null ? 'var(--ink-300)' : qtyInk(q) }}>
                            {q == null ? '—' : q}
                          </div>
                        ))}
                        <div style={{ textAlign: 'right', fontSize: 'var(--t-lead)', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: total < 0 ? 'var(--rose-fg)' : total === 0 ? 'var(--ink-300)' : 'var(--ink-900)' }}>{total}</div>
                      </div>
                    );
                  });
                })}
              </div>
            </div>
          </div>
        ))
      )}

      <div style={{ padding: '13px 17px', borderRadius: 'var(--r-md)', background: 'var(--ink-50)', border: '1px solid var(--ink-200)', fontSize: '12.5px', color: 'var(--ink-600)', lineHeight: 1.6 }}>
        <Trans k='<1>Lecture seule</1> — on ne corrige que son propre stock. Toutes vos références sont là, <2>y compris celles à zéro</2> : c’est justement quand vous n’avez plus rien qu’on regarde chez les autres. Faire venir une pièce passera par l’écran Transferts.' parts={[(x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-900)' }}>{x}</strong>]} /></div>
    </div>
  );
}
